/**
 * Primary action button that triggers the image-to-SVG conversion.
 *
 * Displays a spinner and "Converting..." text while the API call is in progress,
 * and an arrow icon with "Convert to SVG" text when idle. The button is
 * disabled when no file is selected or a conversion is in progress.
 */

import { useTranslation } from 'react-i18next';
import { buttonVariant, ui } from '@sudobility/design';
import { SpinnerIcon, ArrowRightIcon } from './icons';

interface ConvertButtonProps {
  /** Whether the button should be disabled (e.g., no file selected). */
  disabled: boolean;
  /** Whether a conversion is currently in progress. */
  loading: boolean;
  /** Whether an image upload is in progress. */
  uploading?: boolean;
  /** Called when the user clicks the button. */
  onClick: () => void;
}

export default function ConvertButton({
  disabled,
  loading,
  uploading,
  onClick,
}: ConvertButtonProps) {
  const { t } = useTranslation('conversion');

  return (
    <div className="w-full">
      <button
        onClick={onClick}
        disabled={disabled || loading}
        className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold text-sm ${ui.transition.all} ${
          disabled || loading
            ? `${ui.states.disabled} ${ui.background.muted} ${ui.text.muted} cursor-not-allowed`
            : `${buttonVariant('primary')} shadow-md hover:shadow-lg`
        }`}
      >
        {uploading ? (
          <>
            <SpinnerIcon className="animate-spin h-4 w-4" />
            {t('uploading', { defaultValue: 'Uploading...' })}
          </>
        ) : loading ? (
          <>
            <SpinnerIcon className="animate-spin h-4 w-4" />
            {t('converting')}
          </>
        ) : (
          <>
            <ArrowRightIcon className="w-4 h-4" />
            {t('convertToSvg')}
          </>
        )}
      </button>
    </div>
  );
}
